import { Button } from '@citizen-walk/shared-ui';
import type {
  AiOutputCodeListStringFieldDef,
  AiOutputEnumArrayFieldDef,
  AiOutputFieldDef,
  AiOutputFieldValueSource,
  AiOutputObjectArrayFieldDef,
  AiOutputTextFieldDef,
} from '@citizen-walk/shared-types';
import { useState } from 'react';

/**
 * AI 出力フォーマット (M3) の `fields[]` (列名動的化レベル 3、docs/105) 編集 UI 共通モジュール。
 *
 * 元は `AiOutputFormatsPage.tsx` に module-private で定義されていた FieldEditor 一式と純関数を、
 * AI 解析統合エディタ (AiAnalysisEditorPage) からも再利用するため本ファイルへ切り出した。
 * これにより M3 ページと統合エディタが同じ fields[] 編集部品を共有し、二重実装を回避する
 * (CLAUDE.md「再利用できる既存コンポーネントは切り出して再利用」)。
 *
 * 提供物:
 *   - 型/定数: OutputFieldKind / ChildFieldKind / KIND_LABEL / INPUT_CLASS
 *   - 純関数: emptyFieldOfKind / emptyChildField / cloneField / cleanField /
 *             cleanSource / cleanReasonField / parseNumOrUndef
 *   - コンポーネント: FieldEditor (主役) / ChildFieldEditor / FieldKindInputs /
 *             SourceEditor / ReasonFieldEditor / CommonFieldInputs
 *
 * 設計: FieldEditor は childKind を除き state を持たない制御コンポーネント。fields:AiOutputFieldDef[]
 * の state とハンドラ (add/update/remove/move/addChild/updateChild/removeChild) は呼び出し側が集約する。
 */

export const INPUT_CLASS =
  'w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400';

/** fields[] の row レベルで使う kind の判別ラベル (UI セレクトの値)。 */
export type OutputFieldKind = AiOutputFieldDef['kind'];

/** object-array の itemFields に追加できる子フィールド種別 (1 階層のみ)。 */
export type ChildFieldKind = 'text' | 'enum-array' | 'code-list-string';

export const KIND_LABEL: Record<OutputFieldKind, string> = {
  text: 'text (単一文字列)',
  'enum-array': 'enum-array (列挙配列)',
  'code-list-string': 'code-list-string (コード列)',
  'object-array': 'object-array (オブジェクト配列)',
};

/** number 入力の onChange を「空欄 → undefined / 数値」に変換。 */
export function parseNumOrUndef(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

/** 指定 kind の空フィールドを生成 (追加時の初期値)。 */
export function emptyFieldOfKind(kind: OutputFieldKind): AiOutputFieldDef {
  switch (kind) {
    case 'text':
      return { kind: 'text', key: '', label: '', definition: '' };
    case 'enum-array':
      return {
        kind: 'enum-array',
        key: '',
        label: '',
        definition: '',
        source: { type: 'inline', values: [] },
      };
    case 'code-list-string':
      return {
        kind: 'code-list-string',
        key: '',
        label: '',
        definition: '',
        separator: ',',
        source: { type: 'inline', values: [] },
      };
    case 'object-array':
      return { kind: 'object-array', key: '', label: '', definition: '', itemFields: [] };
  }
}

/** 子フィールド (object-array.itemFields[]) の空フィールド生成。 */
export function emptyChildField(kind: ChildFieldKind): AiOutputFieldDef {
  return emptyFieldOfKind(kind);
}

/** AiOutputFieldDef を deep clone (draft 編集で原本を破壊しないため)。 */
export function cloneField(f: AiOutputFieldDef): AiOutputFieldDef {
  if (f.kind === 'object-array') {
    return { ...f, itemFields: f.itemFields ? f.itemFields.map(cloneField) : [] };
  }
  if (f.kind === 'enum-array' || f.kind === 'code-list-string') {
    // source 欠落データ (手編集 JSON 取込・部分的な地区上書き等) でも読込時に落ちないよう、
    // 欠けていれば空のインライン許可値へフォールバックする (本クローンが今回のクラッシュ起点)。
    const rawSource = (f as { source?: AiOutputFieldValueSource }).source;
    const source: AiOutputFieldValueSource =
      rawSource?.type === 'inline'
        ? { type: 'inline', values: [...(rawSource.values ?? [])] }
        : rawSource?.type === 'master'
          ? { ...rawSource }
          : { type: 'inline', values: [] };
    const cloned = { ...f, source } as AiOutputEnumArrayFieldDef | AiOutputCodeListStringFieldDef;
    if (f.reasonField) cloned.reasonField = { ...f.reasonField };
    return cloned;
  }
  return { ...f };
}

export function cleanSource(s: AiOutputFieldValueSource | undefined): AiOutputFieldValueSource {
  if (s?.type === 'master') {
    return { type: 'master', masterId: (s.masterId ?? '').trim(), valueFrom: s.valueFrom };
  }
  if (s?.type === 'inline') {
    return {
      type: 'inline',
      values: (s.values ?? []).map((v) => v.trim()).filter((v) => v.length > 0),
    };
  }
  // source 欠落・不正は空のインライン許可値へフォールバック (保存時クラッシュ回避)。
  return { type: 'inline', values: [] };
}

export function cleanReasonField(
  rf: AiOutputTextFieldDef | undefined,
): AiOutputTextFieldDef | undefined {
  if (!rf) return undefined;
  const key = rf.key.trim();
  if (!key) return undefined;
  return {
    kind: 'text',
    key,
    label: (rf.label ?? '').trim(),
    definition: (rf.definition ?? '').trim(),
    ...(rf.showInPostDetail === false ? { showInPostDetail: false } : {}),
  };
}

/**
 * save 用に fields[] を軽く clean する。
 *  - key/label/definition は trim
 *  - reasonField は key が空欄なら除去
 *  - object-array は itemFields も再帰的に clean、key 空欄の子フィールドは除外
 *  - 空文字の任意フィールド (label/definition/separator) は省略はせず trim のみ
 *
 * key 空欄の行の除外はここでは行わず validate 側で error にする (admin に気付かせる)。
 */
export function cleanField(f: AiOutputFieldDef): AiOutputFieldDef {
  const out = cleanFieldBody(f);
  // docs/29 §5-1: 投稿詳細に表示しない印 (false のときだけ残す。未設定 = 表示)。
  if (f.showInPostDetail === false) out.showInPostDetail = false;
  return out;
}

function cleanFieldBody(f: AiOutputFieldDef): AiOutputFieldDef {
  const base = {
    key: f.key.trim(),
    label: (f.label ?? '').trim(),
    definition: (f.definition ?? '').trim(),
  };
  if (f.kind === 'text') {
    const out: AiOutputTextFieldDef = { kind: 'text', ...base };
    if (typeof f.lengthMin === 'number' && !Number.isNaN(f.lengthMin)) out.lengthMin = f.lengthMin;
    if (typeof f.lengthMax === 'number' && !Number.isNaN(f.lengthMax)) out.lengthMax = f.lengthMax;
    if (f.optional) out.optional = true;
    return out;
  }
  if (f.kind === 'enum-array') {
    const out: AiOutputEnumArrayFieldDef = {
      kind: 'enum-array',
      ...base,
      source: cleanSource(f.source),
    };
    if (typeof f.maxItems === 'number' && !Number.isNaN(f.maxItems)) out.maxItems = f.maxItems;
    const rf = cleanReasonField(f.reasonField);
    if (rf) out.reasonField = rf;
    return out;
  }
  if (f.kind === 'code-list-string') {
    const out: AiOutputCodeListStringFieldDef = {
      kind: 'code-list-string',
      ...base,
      source: cleanSource(f.source),
    };
    const sep = (f.separator ?? '').trim();
    if (sep) out.separator = sep;
    if (typeof f.maxItems === 'number' && !Number.isNaN(f.maxItems)) out.maxItems = f.maxItems;
    const rf = cleanReasonField(f.reasonField);
    if (rf) out.reasonField = rf;
    return out;
  }
  // object-array
  const out: AiOutputObjectArrayFieldDef = { kind: 'object-array', key: base.key };
  if (base.label) out.label = base.label;
  if (base.definition) out.definition = base.definition;
  if (typeof f.minItems === 'number' && !Number.isNaN(f.minItems)) out.minItems = f.minItems;
  if (typeof f.maxItems === 'number' && !Number.isNaN(f.maxItems)) out.maxItems = f.maxItems;
  const children = (f.itemFields ?? [])
    .filter((c) => c.key.trim().length > 0)
    .map((c) => cleanField(c));
  if (children.length > 0) out.itemFields = children;
  return out;
}

/** AiOutputFieldValueSource を編集する共通エディタ。 */
export function SourceEditor({
  source: rawSource,
  onChange,
  idPrefix,
}: {
  source: AiOutputFieldValueSource | undefined;
  onChange: (next: AiOutputFieldValueSource) => void;
  idPrefix: string;
}) {
  // source 欠落データでも編集 UI を壊さない (空のインライン許可値として表示)。
  const source: AiOutputFieldValueSource = rawSource ?? { type: 'inline', values: [] };
  return (
    <div className="grid grid-cols-1 gap-2">
      <div>
        <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-srctype`}>
          値ソース (source.type)
        </label>
        <select
          id={`${idPrefix}-srctype`}
          value={source.type}
          onChange={(e) =>
            onChange(
              e.target.value === 'master'
                ? { type: 'master', masterId: '', valueFrom: 'category' }
                : { type: 'inline', values: [] },
            )
          }
          className={INPUT_CLASS}
          aria-label={`${idPrefix} 値ソース種別`}
        >
          <option value="inline">inline (インライン許可リスト)</option>
          <option value="master">master (カテゴリマスタ参照)</option>
        </select>
      </div>
      {source.type === 'master' ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div>
            <label
              className="block text-[10px] text-slate-500 mb-1"
              htmlFor={`${idPrefix}-masterid`}
            >
              masterId
            </label>
            <input
              id={`${idPrefix}-masterid`}
              type="text"
              value={source.masterId ?? ''}
              onChange={(e) => onChange({ ...source, masterId: e.target.value })}
              className={INPUT_CLASS}
              placeholder="例: ecw-emotion"
              aria-label={`${idPrefix} masterId`}
            />
          </div>
          <div>
            <label
              className="block text-[10px] text-slate-500 mb-1"
              htmlFor={`${idPrefix}-valuefrom`}
            >
              valueFrom
            </label>
            <select
              id={`${idPrefix}-valuefrom`}
              value={source.valueFrom}
              onChange={(e) =>
                onChange({
                  ...source,
                  valueFrom: e.target.value as Extract<
                    AiOutputFieldValueSource,
                    { type: 'master' }
                  >['valueFrom'],
                })
              }
              className={INPUT_CLASS}
              aria-label={`${idPrefix} valueFrom`}
            >
              <option value="category">category (表示名)</option>
              <option value="major-category-code">major-category-code (A-01 形式)</option>
              <option value="major-code">major-code (majorCd)</option>
              <option value="category-code">category-code (categoryCd)</option>
            </select>
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-inline`}>
            許可値 (カンマ区切り)
          </label>
          <input
            id={`${idPrefix}-inline`}
            type="text"
            value={(source.values ?? []).join(', ')}
            onChange={(e) =>
              onChange({ type: 'inline', values: e.target.value.split(',').map((v) => v.trim()) })
            }
            className={INPUT_CLASS}
            placeholder="例: 喜び, 信頼, 期待"
            aria-label={`${idPrefix} インライン許可値`}
          />
        </div>
      )}
    </div>
  );
}

/** 任意の理由テキストフィールド (reasonField) を有効化チェック付きで編集。 */
export function ReasonFieldEditor({
  reasonField,
  onChange,
  idPrefix,
}: {
  reasonField: AiOutputTextFieldDef | undefined;
  onChange: (next: AiOutputTextFieldDef | undefined) => void;
  idPrefix: string;
}) {
  const enabled = reasonField !== undefined;
  return (
    <div className="border border-slate-200 rounded p-2">
      <label className="flex items-center gap-2 text-[11px] text-slate-600">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) =>
            onChange(
              e.target.checked ? { kind: 'text', key: '', label: '', definition: '' } : undefined,
            )
          }
          aria-label={`${idPrefix} 理由フィールド (reasonField) を有効化`}
        />
        理由フィールド (reasonField) を付ける
      </label>
      {enabled && reasonField && (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3 mt-2">
          <input
            type="text"
            value={reasonField.key}
            onChange={(e) => onChange({ ...reasonField, key: e.target.value })}
            className={INPUT_CLASS}
            placeholder="key (例: emotion_reason)"
            aria-label={`${idPrefix} reasonField key`}
          />
          <input
            type="text"
            value={reasonField.label}
            onChange={(e) => onChange({ ...reasonField, label: e.target.value })}
            className={INPUT_CLASS}
            placeholder="label"
            aria-label={`${idPrefix} reasonField label`}
          />
          <input
            type="text"
            value={reasonField.definition}
            onChange={(e) => onChange({ ...reasonField, definition: e.target.value })}
            className={INPUT_CLASS}
            placeholder="definition"
            aria-label={`${idPrefix} reasonField definition`}
          />
        </div>
      )}
    </div>
  );
}

/** key / label / definition の共通 3 列入力。 */
export function CommonFieldInputs({
  field,
  onChange,
  idPrefix,
  keyRequired = true,
}: {
  field: AiOutputFieldDef;
  onChange: (patch: { key?: string; label?: string; definition?: string }) => void;
  idPrefix: string;
  keyRequired?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
      <div>
        <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-key`}>
          key {keyRequired && <span className="text-danger-600">*</span>}
        </label>
        <input
          id={`${idPrefix}-key`}
          type="text"
          value={field.key}
          onChange={(e) => onChange({ key: e.target.value })}
          className={INPUT_CLASS}
          placeholder="JSON キー名"
          aria-label={`${idPrefix} key`}
        />
      </div>
      <div>
        <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-label`}>
          label
        </label>
        <input
          id={`${idPrefix}-label`}
          type="text"
          value={field.label ?? ''}
          onChange={(e) => onChange({ label: e.target.value })}
          className={INPUT_CLASS}
          placeholder="表示ラベル"
          aria-label={`${idPrefix} label`}
        />
      </div>
      <div>
        <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-def`}>
          definition
        </label>
        <input
          id={`${idPrefix}-def`}
          type="text"
          value={field.definition ?? ''}
          onChange={(e) => onChange({ definition: e.target.value })}
          className={INPUT_CLASS}
          placeholder="AI への指示文"
          aria-label={`${idPrefix} definition`}
        />
      </div>
    </div>
  );
}

/** 1 フィールド (top-level or child) の kind 別入力。child では object-array を出さない。 */
export function FieldKindInputs({
  field,
  onChange,
  idPrefix,
}: {
  field: AiOutputFieldDef;
  onChange: (next: AiOutputFieldDef) => void;
  idPrefix: string;
}) {
  if (field.kind === 'text') {
    return (
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 mt-2">
        <div>
          <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-lmin`}>
            lengthMin
          </label>
          <input
            id={`${idPrefix}-lmin`}
            type="number"
            value={field.lengthMin ?? ''}
            onChange={(e) => onChange({ ...field, lengthMin: parseNumOrUndef(e.target.value) })}
            className={INPUT_CLASS}
            aria-label={`${idPrefix} lengthMin`}
          />
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-lmax`}>
            lengthMax
          </label>
          <input
            id={`${idPrefix}-lmax`}
            type="number"
            value={field.lengthMax ?? ''}
            onChange={(e) => onChange({ ...field, lengthMax: parseNumOrUndef(e.target.value) })}
            className={INPUT_CLASS}
            aria-label={`${idPrefix} lengthMax`}
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-[11px] text-slate-600 pb-1">
            <input
              type="checkbox"
              checked={field.optional ?? false}
              onChange={(e) => onChange({ ...field, optional: e.target.checked })}
              aria-label={`${idPrefix} optional`}
            />
            optional (任意フィールド)
          </label>
        </div>
      </div>
    );
  }
  if (field.kind === 'enum-array') {
    return (
      <div className="grid grid-cols-1 gap-2 mt-2">
        <div className="md:w-1/3">
          <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-maxitems`}>
            maxItems
          </label>
          <input
            id={`${idPrefix}-maxitems`}
            type="number"
            value={field.maxItems ?? ''}
            onChange={(e) => onChange({ ...field, maxItems: parseNumOrUndef(e.target.value) })}
            className={INPUT_CLASS}
            aria-label={`${idPrefix} maxItems`}
          />
        </div>
        <SourceEditor
          source={field.source}
          onChange={(source) => onChange({ ...field, source })}
          idPrefix={idPrefix}
        />
        <ReasonFieldEditor
          reasonField={field.reasonField}
          onChange={(reasonField) => onChange({ ...field, reasonField })}
          idPrefix={idPrefix}
        />
      </div>
    );
  }
  if (field.kind === 'code-list-string') {
    return (
      <div className="grid grid-cols-1 gap-2 mt-2">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div>
            <label className="block text-[10px] text-slate-500 mb-1" htmlFor={`${idPrefix}-sep`}>
              separator (既定 &quot;,&quot;)
            </label>
            <input
              id={`${idPrefix}-sep`}
              type="text"
              value={field.separator ?? ''}
              onChange={(e) => onChange({ ...field, separator: e.target.value })}
              className={INPUT_CLASS}
              placeholder=","
              aria-label={`${idPrefix} separator`}
            />
          </div>
          <div>
            <label
              className="block text-[10px] text-slate-500 mb-1"
              htmlFor={`${idPrefix}-maxitems`}
            >
              maxItems
            </label>
            <input
              id={`${idPrefix}-maxitems`}
              type="number"
              value={field.maxItems ?? ''}
              onChange={(e) => onChange({ ...field, maxItems: parseNumOrUndef(e.target.value) })}
              className={INPUT_CLASS}
              aria-label={`${idPrefix} maxItems`}
            />
          </div>
        </div>
        <SourceEditor
          source={field.source}
          onChange={(source) => onChange({ ...field, source })}
          idPrefix={idPrefix}
        />
        <ReasonFieldEditor
          reasonField={field.reasonField}
          onChange={(reasonField) => onChange({ ...field, reasonField })}
          idPrefix={idPrefix}
        />
      </div>
    );
  }
  // object-array の min/max は FieldEditor 側で扱う (children も含むため)
  return null;
}

/** object-array の子フィールド 1 行 (1 階層のみ、text / enum-array / code-list-string)。 */
export function ChildFieldEditor({
  child,
  parentIndex,
  childIndex,
  onChange,
  onRemove,
}: {
  child: AiOutputFieldDef;
  parentIndex: number;
  childIndex: number;
  onChange: (next: AiOutputFieldDef) => void;
  onRemove: () => void;
}) {
  const idPrefix = `m3-field-${parentIndex}-child-${childIndex}`;
  return (
    <div className="border border-slate-200 rounded p-2 bg-white">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-medium text-slate-500">
          子フィールド {childIndex + 1}: {KIND_LABEL[child.kind]}
        </span>
        <Button
          variant="danger"
          size="sm"
          onClick={onRemove}
          aria-label={`フィールド ${parentIndex + 1} の子フィールド ${childIndex + 1} 削除`}
        >
          ✕
        </Button>
      </div>
      <CommonFieldInputs
        field={child}
        onChange={(patch) => onChange({ ...child, ...patch } as AiOutputFieldDef)}
        idPrefix={idPrefix}
      />
      <FieldKindInputs field={child} onChange={onChange} idPrefix={idPrefix} />
    </div>
  );
}

/** top-level fields[] の 1 要素エディタ。 */
export function FieldEditor({
  index,
  total,
  field,
  onChange,
  onRemove,
  onMove,
  onAddChild,
  onUpdateChild,
  onRemoveChild,
}: {
  index: number;
  total: number;
  field: AiOutputFieldDef;
  onChange: (next: AiOutputFieldDef) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  onAddChild: (kind: ChildFieldKind) => void;
  onUpdateChild: (childIndex: number, child: AiOutputFieldDef) => void;
  onRemoveChild: (childIndex: number) => void;
}) {
  const idPrefix = `m3-field-${index}`;
  const [childKind, setChildKind] = useState<ChildFieldKind>('text');
  return (
    <div className="border border-slate-300 rounded-md p-3 bg-slate-50">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="text-xs font-bold text-slate-700">
          フィールド {index + 1}: {KIND_LABEL[field.kind]}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            aria-label={`フィールド ${index + 1} を上へ`}
          >
            ↑
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            aria-label={`フィールド ${index + 1} を下へ`}
          >
            ↓
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onRemove}
            aria-label={`フィールド ${index + 1} 削除`}
          >
            ✕
          </Button>
        </div>
      </div>
      <CommonFieldInputs
        field={field}
        onChange={(patch) => onChange({ ...field, ...patch } as AiOutputFieldDef)}
        idPrefix={idPrefix}
        keyRequired
      />
      {/* docs/29 §5-1: 解析はするが投稿詳細には出さない (データ・分析・エクスポートには残る)。 */}
      <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-700">
        <input
          type="checkbox"
          checked={field.showInPostDetail !== false}
          onChange={(e) =>
            onChange({
              ...field,
              showInPostDetail: e.target.checked ? undefined : false,
            } as AiOutputFieldDef)
          }
          aria-label={`フィールド ${index + 1} を投稿詳細に表示する`}
        />
        投稿詳細に表示する
        <span className="text-[10px] text-slate-500">
          （外しても AI は生成し、分析・エクスポートには残ります）
        </span>
      </label>
      {field.kind === 'object-array' ? (
        <div className="mt-2 grid grid-cols-1 gap-2">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div>
              <label
                className="block text-[10px] text-slate-500 mb-1"
                htmlFor={`${idPrefix}-minitems`}
              >
                minItems
              </label>
              <input
                id={`${idPrefix}-minitems`}
                type="number"
                value={field.minItems ?? ''}
                onChange={(e) => onChange({ ...field, minItems: parseNumOrUndef(e.target.value) })}
                className={INPUT_CLASS}
                aria-label={`${idPrefix} minItems`}
              />
            </div>
            <div>
              <label
                className="block text-[10px] text-slate-500 mb-1"
                htmlFor={`${idPrefix}-maxitems`}
              >
                maxItems
              </label>
              <input
                id={`${idPrefix}-maxitems`}
                type="number"
                value={field.maxItems ?? ''}
                onChange={(e) => onChange({ ...field, maxItems: parseNumOrUndef(e.target.value) })}
                className={INPUT_CLASS}
                aria-label={`${idPrefix} maxItems`}
              />
            </div>
          </div>
          <div className="border-t border-slate-200 pt-2">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <span className="text-[11px] font-medium text-slate-600">
                子フィールド (itemFields、{(field.itemFields ?? []).length} 件)
              </span>
              <div className="flex items-center gap-1">
                <select
                  value={childKind}
                  onChange={(e) => setChildKind(e.target.value as ChildFieldKind)}
                  className={INPUT_CLASS}
                  style={{ width: 'auto', maxWidth: '100%' }}
                  aria-label={`フィールド ${index + 1} 子フィールド種別`}
                >
                  <option value="text">text</option>
                  <option value="enum-array">enum-array</option>
                  <option value="code-list-string">code-list-string</option>
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onAddChild(childKind)}
                  aria-label={`フィールド ${index + 1} に子フィールドを追加`}
                >
                  ＋ 子追加
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mb-2">
              子フィールドは 1 階層のみ (子の object-array は非対応)。空のまま保存すると
              classificationAxes / assessment から itemFields が自動補完されます。
            </p>
            {(field.itemFields ?? []).length === 0 ? (
              <p className="text-[11px] text-slate-500">
                子フィールドがありません (空なら自動補完)。
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {(field.itemFields ?? []).map((child, ci) => (
                  <ChildFieldEditor
                    key={ci}
                    child={child}
                    parentIndex={index}
                    childIndex={ci}
                    onChange={(next) => onUpdateChild(ci, next)}
                    onRemove={() => onRemoveChild(ci)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <FieldKindInputs field={field} onChange={onChange} idPrefix={idPrefix} />
      )}
    </div>
  );
}
